import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

interface Problem {
  problem: string;
  passionArea: string;
  createdAt: Date;
}

const ProblemIdentifier: React.FC = () => {
  const { currentUser } = useApp();
  const [problems, setProblems] = useState<Problem[]>([]);
  const [passions, setPassions] = useState<string[]>([]);
  const [newProblem, setNewProblem] = useState('');
  const [selectedPassion, setSelectedPassion] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editProblem, setEditProblem] = useState('');
  const [editPassionArea, setEditPassionArea] = useState('');

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setPassions(data.passions?.items || []);
          setProblems(data.problems || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async (updatedProblems: Problem[]) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        await setDoc(docRef, {
          ...docSnap.data(),
          problems: updatedProblems,
          lastUpdated: new Date(),
        });
      } else {
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          problems: updatedProblems,
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const addProblem = () => {
    if (newProblem.trim() && selectedPassion) {
      const updated = [
        ...problems,
        {
          problem: newProblem.trim(),
          passionArea: selectedPassion,
          createdAt: new Date(),
        },
      ];
      setProblems(updated);
      setNewProblem('');
      setSelectedPassion('');
      saveData(updated);
    }
  };

  const removeProblem = (index: number) => {
    const updated = problems.filter((_, i) => i !== index);
    setProblems(updated);
    saveData(updated);
  };

  // Edit problem functions
  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditProblem(problems[index].problem);
    setEditPassionArea(problems[index].passionArea);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditProblem('');
    setEditPassionArea('');
  };

  const saveEdit = () => {
    if (editingIndex !== null && editProblem.trim() && editPassionArea) {
      const updated = [...problems];
      updated[editingIndex] = {
        ...updated[editingIndex],
        problem: editProblem.trim(),
        passionArea: editPassionArea,
      };
      setProblems(updated);
      saveData(updated);
      setEditingIndex(null);
      setEditProblem('');
      setEditPassionArea('');
    }
  };

  return (
    <SideHustleLayout currentPage="problems">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-4xl w-full">
          <h1 className="text-5xl font-bold text-center mb-6">Problem Identifier</h1>
          <p className="text-xl text-gray-700 text-center mb-12">
            In the areas that you are passionate about, what are some problems that you see?
          </p>

          {/* Add New Problem Card */}
          <div className="bg-[#86C87B] p-8 rounded-3xl shadow-lg mb-6">
            <h2 className="text-2xl font-bold mb-4 underline">New Problem:</h2>

            <div className="space-y-4">
              <textarea
                value={newProblem}
                onChange={(e) => setNewProblem(e.target.value)}
                placeholder="Enter the problem"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />

              <div>
                <select
                  value={selectedPassion}
                  onChange={(e) => setSelectedPassion(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Passion Area(s)</option>
                  {passions.map((passion, index) => (
                    <option key={index} value={passion}>
                      {passion}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={addProblem}
                disabled={!newProblem.trim() || !selectedPassion}
                className="w-full bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Problem
              </button>
            </div>
          </div>

          {/* Existing Problems */}
          <div className="space-y-4">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="bg-primary p-6 rounded-3xl shadow-lg text-white relative"
              >
                {editingIndex === index ? (
                  <div className="space-y-4">
                    <textarea
                      value={editProblem}
                      onChange={(e) => setEditProblem(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none text-gray-800"
                      autoFocus
                    />
                    <select
                      value={editPassionArea}
                      onChange={(e) => setEditPassionArea(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-gray-800"
                    >
                      <option value="">Select Passion Area(s)</option>
                      {passions.map((passion, idx) => (
                        <option key={idx} value={passion}>
                          {passion}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        className="flex-1 bg-white text-primary px-6 py-3 rounded-xl hover:bg-gray-100 font-semibold"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="flex-1 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-4 right-4 flex gap-2">
                      <button
                        onClick={() => startEditing(index)}
                        className="text-white hover:text-gray-200 font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeProblem(index)}
                        className="text-white hover:text-red-200 font-bold text-xl"
                      >
                        ×
                      </button>
                    </div>
                    <p className="text-xl font-semibold mb-2 pr-24">{problem.problem}</p>
                    <p className="text-white/80">Passion Area: {problem.passionArea}</p>
                  </>
                )}
              </div>
            ))}
          </div>

          {passions.length === 0 && (
            <div className="text-center text-gray-600 mt-8">
              <p className="mb-2">
                You haven't added any passions yet!
              </p>
              <p>
                Visit the <strong>Talents & Passions</strong> page to add some first.
              </p>
            </div>
          )}

          {saving && (
            <div className="text-center mt-6 text-primary font-semibold">
              Saving...
            </div>
          )}
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default ProblemIdentifier;
